import React from "react";
import { Helmet } from "react-helmet-async";
import AppNav from "../components/appNav";
import Footer from "../components/footer";
import { useTranslation } from "react-i18next";

const Section = ({
  num,
  title,
  children,
}: {
  num: string;
  title?: string;
  children: React.ReactNode;
}) => (
  <section className="swiss-section">
    <div className="swiss-grid">
      <div className="col-span-12 lg:col-span-2 mb-6 lg:mb-0">
        <span className="swiss-num">{num}</span>
      </div>
      <div className="col-span-12 lg:col-span-10">
        {title && (
          <h2 className="text-h2 font-bold tracking-tight text-ink mb-7 max-w-[22ch]">{title}</h2>
        )}
        {children}
      </div>
    </div>
  </section>
);

const AIAgentsPage = () => {
  const { t } = useTranslation();

  return (
    <>
      <Helmet>
        <title>Hotel AI Assistant | Document Management Solutions | KODE</title>
        <meta name="description" content="Transform hotel operations with KODE's AI assistant. Instant access to policies, procedures, and training materials. 95% accuracy, 40-60% faster staff training." />
      </Helmet>

      <div className="min-h-screen bg-paper text-ink">
        <header className="sticky top-0 z-50 bg-paper/95 backdrop-blur-[2px] border-b border-ink-14">
          <AppNav />
        </header>

        <main>
          {/* 01 / Hero */}
          <section className="swiss-section !border-t-0 pt-7 lg:pt-9">
            <div className="swiss-grid">
              <div className="col-span-12 lg:col-span-2 mb-6 lg:mb-0">
                <span className="swiss-num">01 / AI</span>
              </div>
              <div className="col-span-12 lg:col-span-10">
                <h1 className="text-[clamp(2.5rem,5.5vw,5.5rem)] font-black tracking-tightest leading-[0.95] text-ink max-w-[16ch] mb-7">
                  {t('aiAgents.hero.title')}
                </h1>
                <p className="text-body-lg text-ink-70 max-w-prose mb-7">
                  {t('aiAgents.hero.subtitle')}
                </p>
                <p className="text-body text-ink-70 max-w-prose">
                  {t('aiAgents.hero.description')}
                </p>
              </div>
            </div>
          </section>

          {/* 02 / Overview */}
          <Section num={`02 / ${t('Overview')}`} title={t('aiAgents.overview.title')}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border-t border-ink pt-4">
                <h3 className="text-h4 font-medium tracking-tight text-ink mb-3">
                  {t('aiAgents.overview.challenge.title')}
                </h3>
                <p className="text-body text-ink-70 max-w-measure">
                  {t('aiAgents.overview.challenge.description')}
                </p>
              </div>
              <div className="border-t border-ink pt-4">
                <h3 className="text-h4 font-medium tracking-tight text-ink mb-3">
                  {t('aiAgents.overview.solution.title')}
                </h3>
                <p className="text-body text-ink-70 max-w-measure">
                  {t('aiAgents.overview.solution.description')}
                </p>
              </div>
            </div>
          </Section>

          {/* 03 / How it works */}
          <Section num={`03 / ${t('Approach')}`} title={t('aiAgents.howItWorks.title')}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-9">
              {(["smartIntegration", "aiTechnology"] as const).map((k) => (
                <div key={k} className="border-t border-ink pt-4">
                  <h3 className="text-h4 font-medium tracking-tight text-ink mb-3">
                    {t(`aiAgents.howItWorks.${k}.title`)}
                  </h3>
                  <p className="text-body text-ink-70 max-w-measure mb-5">
                    {t(`aiAgents.howItWorks.${k}.description`)}
                  </p>
                  <ul className="flex flex-col gap-2 text-body text-ink-70">
                    {[0, 1, 2, 3].map((i) => (
                      <li key={i} className="flex gap-3">
                        <span className="text-ink-50">—</span>
                        <span>{t(`aiAgents.howItWorks.${k}.features.${i}`)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Section>

          {/* 04 / Tiers */}
          <Section num={`04 / ${t('Pricing')}`} title={t('aiAgents.serviceTiers.title')}>
            <ul className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {(["essential", "professional", "enterprise"] as const).map((tier) => (
                <li key={tier} className="border-t border-ink pt-4 flex flex-col">
                  <span className="swiss-caption mb-3">
                    {tier === "professional" ? t('aiAgents.serviceTiers.professional.badge') : tier.toUpperCase()}
                  </span>
                  <h3 className="text-h3 font-medium tracking-tight text-ink mb-3">
                    {t(`aiAgents.serviceTiers.${tier}.title`)}
                  </h3>
                  <p className="text-[clamp(2rem,3vw,2.5rem)] font-black tracking-tighter leading-none text-ink mb-5">
                    {t(`aiAgents.serviceTiers.${tier}.price`)}
                  </p>
                  <p className="text-body text-ink-70 max-w-measure mb-5">
                    {t(`aiAgents.serviceTiers.${tier}.description`)}
                  </p>
                  <ul className="flex flex-col gap-2 text-body text-ink-70 flex-1 mb-7">
                    {[0, 1, 2, 3, ...(tier === "essential" ? [] : [4])].map((i) => (
                      <li key={i} className="flex gap-3">
                        <span className="text-ink-50">—</span>
                        <span>{t(`aiAgents.serviceTiers.${tier}.features.${i}`)}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href="https://wa.me/34636349614"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-secondary w-full"
                  >
                    {tier === "enterprise" ? "Contact Sales" : tier === "professional" ? "Choose Professional" : "Get Started"}
                  </a>
                </li>
              ))}
            </ul>
          </Section>

          {/* 05 / Benefits */}
          <Section num={`05 / ${t('Benefits')}`} title={t('aiAgents.keyBenefits.title')}>
            <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-7">
              {(["efficiency", "cost", "scalability", "accuracy", "availability", "innovation"] as const).map((k) => (
                <li key={k} className="border-t border-ink pt-4">
                  <h4 className="text-h4 font-medium tracking-tight text-ink mb-3">
                    {t(`aiAgents.keyBenefits.${k}.title`)}
                  </h4>
                  <p className="text-body text-ink-70 max-w-measure">
                    {t(`aiAgents.keyBenefits.${k}.description`)}
                  </p>
                </li>
              ))}
            </ul>
          </Section>

          {/* 06 / Implementation */}
          <Section num={`06 / ${t('Implementation')}`} title={t('aiAgents.implementation.title')}>
            <ul className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {(["phase1", "phase2", "phase3"] as const).map((phase, i) => (
                <li key={phase} className="border-t border-ink pt-4">
                  <span className="swiss-caption mb-5 block">
                    {String(i + 1).padStart(2, "0")} / {t(`aiAgents.implementation.${phase}.duration`)}
                  </span>
                  <h4 className="text-h4 font-medium tracking-tight text-ink mb-5">
                    {t(`aiAgents.implementation.${phase}.title`)}
                  </h4>
                  <ul className="flex flex-col gap-2 text-body text-ink-70">
                    {[0, 1, 2, 3].map((j) => (
                      <li key={j} className="flex gap-3">
                        <span className="text-ink-50">—</span>
                        <span>{t(`aiAgents.implementation.${phase}.tasks.${j}`)}</span>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </Section>

          {/* 07 / Success */}
          <Section num={`07 / ${t('Results')}`} title={t('aiAgents.successMetrics.title')}>
            <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-10">
              {[
                { value: "40-60%", label: t('aiAgents.successMetrics.operational.training') },
                { value: "95%+", label: t('aiAgents.successMetrics.operational.accuracy') },
                { value: "80%", label: t('aiAgents.successMetrics.operational.resolution') },
              ].map((m) => (
                <div key={m.label} className="border-t border-ink pt-4">
                  <dt className="swiss-caption mb-3">{m.label}</dt>
                  <dd className="text-[clamp(2.5rem,5vw,4rem)] font-black tracking-tighter leading-none text-ink">
                    {m.value}
                  </dd>
                </div>
              ))}
            </dl>
          </Section>

          {/* 08 / CTA */}
          <section className="swiss-section">
            <div className="swiss-grid">
              <div className="col-span-12 lg:col-span-2 mb-6 lg:mb-0">
                <span className="swiss-num">08 / {t('Next')}</span>
              </div>
              <div className="col-span-12 lg:col-span-10">
                <h2 className="text-h2 font-bold tracking-tight text-ink mb-5 max-w-[20ch]">
                  {t('aiAgents.nextSteps.title')}
                </h2>
                <p className="text-body-lg text-ink-70 max-w-prose mb-7">
                  {t('aiAgents.nextSteps.description')}
                </p>
                <a
                  href="https://wa.me/34636349614"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                >
                  {t('aiAgents.nextSteps.cta.demo')}
                </a>
              </div>
            </div>
          </section>

          <Footer />
        </main>
      </div>
    </>
  );
};

export default AIAgentsPage;
